import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import { getTimeline, listSessions } from "../lib/api";
import {
  fmtDateKey,
  fmtRelative,
  fmtDuration,
  basename,
} from "../lib/format";
import {
  FilePlus2,
  FilePenLine,
  FileX2,
  Loader2,
  Clock,
  FileCode2,
} from "lucide-react";

const ICONS = {
  created: FilePlus2,
  modified: FilePenLine,
  deleted: FileX2,
};

export default function TimelinePage() {
  const { projectId } = useParams();
  const [data, setData] = useState(null);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    Promise.all([getTimeline(projectId), listSessions(projectId)])
      .then(([t, s]) => {
        setData(t);
        setSessions(s || []);
      })
      .catch(() => setData({ days: [] }));
  }, [projectId]);

  if (!data) {
    return (
      <AppShell>
        <div className="p-12 text-[#6b7280] flex items-center gap-2">
          <Loader2 className="animate-spin" size={16} /> Loading timeline…
        </div>
      </AppShell>
    );
  }

  // Build a map session_id -> session for fast lookup
  const sessionMap = new Map(sessions.map((s) => [s.id, s]));

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-6 sm:py-10">
        <div className="mb-8">
          <span className="text-[11px] uppercase tracking-[0.18em] text-[#6b7280]">Timeline</span>
          <h1 className="text-3xl tracking-tight text-[#111827] mt-1.5">
            How this project was built
          </h1>
          <p className="text-sm text-[#6b7280] mt-2 max-w-xl">
            Sessions are clusters of focused work. Files inside each block are what you touched.
          </p>
        </div>

        {data.days.length === 0 ? (
          <div className="trace-card p-10 text-center text-[#6b7280]" data-testid="timeline-empty">
            No activity yet. Sync the project to populate the timeline.
          </div>
        ) : (
          <ol className="relative pl-6" data-testid="timeline-days">
            <span className="absolute left-2 top-1 bottom-1 w-px bg-[#e5e7eb]" />
            {data.days.map((day) => {
              // Group day events by session_id
              const grouped = new Map();
              const orphans = [];
              for (const e of day.events) {
                if (e.session_id && sessionMap.has(e.session_id)) {
                  if (!grouped.has(e.session_id)) grouped.set(e.session_id, []);
                  grouped.get(e.session_id).push(e);
                } else {
                  orphans.push(e);
                }
              }
              const sessionEntries = [...grouped.entries()].sort((a, b) => {
                const sa = sessionMap.get(a[0]);
                const sb = sessionMap.get(b[0]);
                return new Date(sb?.end_time || 0) - new Date(sa?.end_time || 0);
              });

              return (
                <li key={day.date} className="mb-12 relative">
                  <span className="absolute -left-[3px] top-1 w-2.5 h-2.5 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500" />
                  <div className="flex items-baseline justify-between gap-3 mb-3">
                    <h2 className="text-base font-semibold text-[#0b1220]">{fmtDateKey(day.date)}</h2>
                    <span className="text-xs text-[#4b5563] font-medium">
                      {grouped.size > 0
                        ? `${grouped.size} ${grouped.size === 1 ? "session" : "sessions"}`
                        : `${day.count} events`}
                    </span>
                  </div>

                  {sessionEntries.map(([sid, events]) => {
                    const s = sessionMap.get(sid);
                    return (
                      <SessionBlock key={sid} session={s} events={events} />
                    );
                  })}

                  {orphans.length > 0 && (
                    <ul className="space-y-2 mt-2">
                      {orphans.slice(0, 20).map((e) => (
                        <RawEventRow key={e.id} e={e} />
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </AppShell>
  );
}

function SessionBlock({ session, events }) {
  if (!session) return null;
  return (
    <div
      className="mb-4 trace-card p-4 hover:-translate-y-0.5 transition-transform duration-300"
      data-testid={`session-block-${session.id}`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.15em] text-[#4b5563] font-bold">
            You worked on
          </div>
          <h3 className="text-base sm:text-lg text-[#0b1220] mt-0.5 leading-snug">
            <span className="gradient-text font-semibold">
              {session.session_note?.trim() || session.topic || session.summary}
            </span>{" "}
            <span className="text-[#4b5563] text-sm font-medium">
              for {fmtDuration(session.duration_seconds)}
            </span>
          </h3>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-[#4b5563] shrink-0 font-medium">
          <span className="flex items-center gap-1">
            <Clock size={11} strokeWidth={2.2} /> {fmtRelative(session.end_time)}
          </span>
          <span className="flex items-center gap-1">
            <FileCode2 size={11} strokeWidth={2.2} /> {events.length}
          </span>
        </div>
      </div>
      <ul className="mt-3 space-y-1.5">
        {events.slice(0, 8).map((e) => {
          const Icon = ICONS[e.event_type] || FilePenLine;
          return (
            <li
              key={e.id}
              className="flex items-center justify-between gap-2 text-[12px] text-[#1f2937]"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Icon
                  size={12}
                  strokeWidth={2.2}
                  className={
                    e.event_type === "created"
                      ? "text-emerald-600"
                      : e.event_type === "deleted"
                      ? "text-rose-600"
                      : "text-[#6b7280]"
                  }
                />
                <span className="font-mono truncate text-[#0b1220] font-medium" title={e.file_path}>
                  {basename(e.file_path)}
                </span>
                {(e.additions > 0 || e.deletions > 0) && (
                  <span className="text-[10.5px] font-mono text-[#4b5563]">
                    {e.additions > 0 && (
                      <span className="text-emerald-600 font-semibold">+{e.additions}</span>
                    )}
                    {e.additions > 0 && e.deletions > 0 && " "}
                    {e.deletions > 0 && (
                      <span className="text-rose-600 font-semibold">-{e.deletions}</span>
                    )}
                  </span>
                )}
              </div>
              {e.commit_message && (
                <span className="text-[10.5px] text-[#4b5563] italic truncate max-w-[55%]">
                  "{e.commit_message}"
                </span>
              )}
            </li>
          );
        })}
        {events.length > 8 && (
          <li className="text-[10.5px] text-[#6b7280] pl-5 font-medium">+{events.length - 8} more files</li>
        )}
      </ul>
    </div>
  );
}

function RawEventRow({ e }) {
  const Icon = ICONS[e.event_type] || FilePenLine;
  return (
    <li className="trace-card p-3 flex items-center justify-between gap-3">
      <div className="flex items-start gap-2 min-w-0">
        <Icon
          size={12}
          strokeWidth={2.2}
          className={
            e.event_type === "created"
              ? "text-emerald-600 mt-0.5"
              : e.event_type === "deleted"
              ? "text-rose-600 mt-0.5"
              : "text-[#6b7280] mt-0.5"
          }
        />
        <div className="min-w-0">
          <div className="font-mono text-[11.5px] text-[#0b1220] truncate font-medium">
            {basename(e.file_path)}
          </div>
          <div className="text-[10.5px] text-[#4b5563] truncate">{e.file_path}</div>
        </div>
      </div>
      <div className="text-[10.5px] text-[#6b7280] font-medium">{fmtRelative(e.event_time)}</div>
    </li>
  );
}
